const EVIDENCE_IDS = {
  teamPhantomBattery: 'team-phantom-battery',
  msessSystemsfair: 'msess-systemsfair',
  sfuHiveLeader: 'sfu-hive-leader',
  duneThumper: 'dune-thumper',
  stormhacksRobotDog: 'stormhacks-robot-dog',
  wheelchairProximityDisplay: 'wheelchair-proximity-display',
} as const

type EvidenceId = (typeof EVIDENCE_IDS)[keyof typeof EVIDENCE_IDS]

type ResumeExperienceItem = {
  id: EvidenceId
  company: string
  role: string
  period: string
  description: string
  bullets: string[]
}

type ResumeProjectItem = {
  id: EvidenceId
  name: string
  period: string
  description: string
  bullets: string[]
}

type HomePageProofPoint = {
  sourceId: EvidenceId
  title: string
  detail: string
}

export const RESUME_PDF_PATH = '/eric_cosma_resume.pdf'

export const resumeData = {
  name: 'Eric Cosma',
  title: 'BASc Mechatronic Systems Engineering Student',
  contact: {
    email: 'eac16@sfu.ca',
    location: 'Coquitlam, BC',
    linkedinPath: 'linkedin.com/in/ericcosma',
  },
  summary:
    'SFU Mechatronic Systems Engineering student with hands-on work in high-voltage EV integration, PCB design, embedded C++, and rapid hardware prototyping.',
  targetRoles: [
    'Mechatronics Engineering Intern',
    'Hardware/Electronics Intern',
    'Embedded Systems Intern',
  ],
  education: {
    school: 'Simon Fraser University, Surrey, BC',
    degree: 'BASc, Mechatronic Systems Engineering',
    coursework: [
      'Electric Circuits',
      'Electronic Circuits',
      'Power Electronics and Electric Machinery',
      'Feedback Control Systems',
      'Signals and Systems',
      'Sensors and Actuators',
    ],
  },
  experience: [
    {
      id: EVIDENCE_IDS.teamPhantomBattery,
      company: 'SFU Team Phantom (Formula SAE Electric Racing Team)',
      role: 'High Voltage Battery Team',
      period: 'Mar 2024 - Present',
      description:
        'Contributed to high-voltage battery and safety systems for an electric Formula SAE race car.',
      bullets: [
        'Integrated and tested the high-voltage system with the inverter and induction motor, achieving successful motor spin tests.',
        'Designed, assembled, and tested a high-voltage pre-charge PCB in Altium Designer to safely energize the inverter and limit inrush current.',
        'Designed and validated a high-voltage safety light PCB using Altium Designer and LTspice to support safe battery disconnect procedures.',
      ],
    },
    {
      id: EVIDENCE_IDS.msessSystemsfair,
      company: 'Mechatronic Systems Engineering Student Society (MSESS)',
      role: 'VP, Professional Relations',
      period: 'May 2024 - May 2025',
      description:
        'Led employer engagement and student-facing professional programming for SFU Surrey mechatronics students.',
      bullets: [
        'Led six months of SystemsFair planning; coordinated 14 employers and 1,000+ attendees.',
        'Organized beginner workshops on SolidWorks and Arduino PWM.',
        'Launched Coffee Mondays to build community among mechatronics students and faculty.',
      ],
    },
    {
      id: EVIDENCE_IDS.sfuHiveLeader,
      company: 'Simon Fraser University',
      role: 'HIVE Leader',
      period: 'Summer 2024, Summer 2025',
      description:
        'Mentored incoming mechatronics students through onboarding content and community events.',
      bullets: [
        'Mentored incoming students through transition support and summer activities.',
        'Created informational posts on academic strategies, annual events, and design teams.',
        'Co-organized pre-term social events to help students build peer connections.',
      ],
    },
  ] as ResumeExperienceItem[],
  projects: [
    {
      id: EVIDENCE_IDS.duneThumper,
      name: 'Dune Thumper (Personal Project)',
      period: 'Jun 2025 - Present',
      description:
        'Near 1:1 Dune thumper replica prototype with custom mechanism and control electronics.',
      bullets: [
        'Modeled and built a near 1:1 Dune thumper prototype using SolidWorks and 3D printing.',
        'Designed a rack-and-pinion mechanism to convert DC motor rotation into linear motion.',
        'Built and tested a custom DC motor driver PCB with multiple operating modes.',
      ],
    },
    {
      id: EVIDENCE_IDS.stormhacksRobotDog,
      name: 'StormHacks Robot Dog',
      period: 'Fall 2025',
      description: '24-hour hackathon build of a four-legged, two-DOF robot dog.',
      bullets: [
        'Built a four-legged, two-DOF robot dog in 24 hours with a four-person team.',
        'Programmed an Arduino Uno over I2C to control eight servos through a 16-channel PWM driver.',
        'Calibrated servo behavior in C++ for coordinated movement.',
      ],
    },
    {
      id: EVIDENCE_IDS.wheelchairProximityDisplay,
      name: 'Wheelchair Proximity Display (Course Project)',
      period: 'Spring 2024',
      description:
        'Led a team project to build a wheelchair object-proximity alert and display system.',
      bullets: [
        'Led a five-student team through the engineering design cycle.',
        'Programmed Arduino (C++) and integrated ultrasonic sensors, switches, buzzer, and color display.',
        'Designed and 3D printed protective enclosures for electronics.',
      ],
    },
  ] as ResumeProjectItem[],
  leadership: [
    {
      organization: 'Mechatronic Systems Engineering Student Society',
      role: 'VP of Services',
      period: 'May 2025 - Present',
      highlights: [
        'Coordinated first-year merchandise orders.',
        'Managed and updated student society website and Discord.',
      ],
    },
    {
      organization: 'Simon Fraser University',
      role: 'Career Fair Ambassador',
      period: 'Fall 2023, Fall 2024, Fall 2025',
      highlights: [
        'Supported vendor logistics and event setup for on-time operations.',
        'Ran event engagement activities and student outreach.',
      ],
    },
  ],
  certifications: [
    {
      name: 'Siemens Mechatronic Systems Certification Program Assistant',
      issuer: 'Simon Fraser University',
      period: 'Summer 2024',
      highlights: [
        'Trained in electrical components, pneumatic/hydraulic control circuits, PLCs, and mechanical components for industrial automation.',
        'Completed hands-on work in TIA Portal programming, relay latching circuits, and SCADA presentations.',
        'Used sequential function charts to diagnose instructor-planted faults in a FESTO assembly line.',
      ],
    },
  ],
  awards: [
    {
      name: "Dean's Honour Roll",
      period: 'Summer 2024',
      detail: 'Awarded for achieving a minimum term GPA (TGPA) of 3.50.',
    },
  ],
  skills: {
    strong: [
      'High-Voltage System Integration',
      'Pre-charge & Safety Circuit Design',
      'Altium Designer',
      'PCB Design & Assembly',
      'C++ / Arduino Controls',
      'SolidWorks & 3D Prototyping',
    ],
    moderate: [
      'LTspice',
      'I2C / PWM Integration',
      'Oscilloscopes & DMMs',
      'Wiring & Crimping',
      'LabVIEW',
      'TIA Portal / SCADA (training)',
    ],
    gaps: [
      'ROS/ROS2 in deployed robotics workflows',
      'Advanced motor control and inverter calibration',
      'Hardware-in-the-loop (HIL) testing workflows',
    ],
  },
}

const linkedinPath = resumeData.contact.linkedinPath.replace(/^https?:\/\//, '').replace(/^\/+/, '')

export const resumeContact = {
  email: resumeData.contact.email,
  location: resumeData.contact.location,
  linkedinUrl: `https://${linkedinPath}`,
}

const resumeHomePage = {
  openToLabel: 'Open To Mechatronics, Robotics, and Embedded Roles',
  roleFocus: 'Robotics + Embedded Systems',
  proofPoints: [
    {
      sourceId: EVIDENCE_IDS.teamPhantomBattery,
      title: 'Formula SAE EV Systems',
      detail:
        'Built and tested high-voltage pre-charge and safety-light PCBs for SFU Team Phantom.',
    },
    {
      sourceId: EVIDENCE_IDS.stormhacksRobotDog,
      title: '24-Hour Robot Build',
      detail:
        'Delivered a four-legged robot dog with calibrated 8-servo I2C motion control at StormHacks.',
    },
    {
      sourceId: EVIDENCE_IDS.msessSystemsfair,
      title: 'Leadership Signal',
      detail: 'Directed SystemsFair planning with 14 employers and 1,000+ student attendees.',
    },
  ] as const satisfies readonly HomePageProofPoint[],
}

const createFeedItem = (
  id: string,
  title: string,
  organization: string,
  period: string,
  summary: string,
  bullets: string[]
) => ({
  id,
  title,
  organization,
  period,
  summary,
  bullets: [...bullets],
})

export const resumeViewModel = {
  name: resumeData.name,
  summary: resumeData.summary,
  contact: resumeContact,
  homepage: resumeHomePage,
  education: {
    degree: resumeData.education.degree,
  },
  experience: resumeData.experience.map((item) =>
    createFeedItem(item.id, item.role, item.company, item.period, item.description, item.bullets)
  ),
  projects: resumeData.projects.map((item) =>
    createFeedItem(
      item.id,
      item.name,
      'Featured Project',
      item.period,
      item.description,
      item.bullets
    )
  ),
  leadership: resumeData.leadership.map((item) => ({
    title: item.role,
    organization: item.organization,
    period: item.period,
  })),
  certifications: resumeData.certifications.map((item) => ({
    title: item.name,
    organization: item.issuer,
    period: item.period,
  })),
  awards: resumeData.awards.map((item) => ({
    title: item.name,
    detail: item.detail,
    period: item.period,
  })),
  skills: {
    coreStrengths: [...resumeData.skills.strong],
    appliedAndDeveloping: [...resumeData.skills.moderate],
    currentLearningFocus: [...resumeData.skills.gaps],
  },
}
